import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useModalManager } from "../../hooks/useModalManager";
import SettingsModal from "../../features/settings/components/SettingsModal";
import { useMerchant } from "../../context/merchant";
import LogoutModal from "../../features/settings/components/LogoutModal";
import "./style.css";

const getMenuItems = (role, draftImageTool) => [
  {
    title: "Захиалга",
    items: [
      {
        to: "orders",
        label: "Захиалгууд",
        icon: "uil-clipboard-notes",
      },
      {
        to: "history",
        label: "Захиалгын түүх",
        icon: "uil-history",
      },
      // {
      //   to: "pre-order-dates",
      //   label: "Хүлээн авах өдөр",
      //   icon: "uil-calendar-alt",
      // },
    ],
  },

  {
    title: "Бүтээгдэхүүн",
    items: [
      {
        to: "edit-products",
        label:
          role === "PRODUCT_MANAGER" || role === "ADMIN"
            ? "Нэмэх, засах"
            : "Засах",
        icon: "uil-edit",
      },
      // Зөвхөн DB-ээс асаасан дэлгүүрт (merchants.draftImageTool=1) харагдана.
      ...(draftImageTool
        ? [
            {
              to: "capture-images",
              label: "Зураггүй бараа",
              icon: "uil-camera",
            },
          ]
        : []),
      {
        to: "add-discount",
        label: "Хямдрал",
        icon: "uil-tag",
      },
    ],
  },

  // CATEGORY_MANAGER + ADMIN → Ангилал харагдана
  ...(role === "CATEGORY_MANAGER" || role === "ADMIN"
    ? [
        {
          title: "Ангилал",
          items: [
            {
              to: "add-category",
              label: "Нэмэх, засах",
              icon: "uil-plus-circle",
            },
          ],
        },
      ]
    : []),

  {
    title: "Шууд хүргэлт",
    items: [
      {
        to: "request-driver",
        label: "Жолооч дуудах",
        icon: "uil-phone",
      },
      {
        to: "delivery-history",
        label: "Дуудлагын түүх",
        icon: "uil-history",
      },
    ],
  },
];

export default function MerchantSideMenu() {
  const { openModal, closeModal, currentModal } = useModalManager();
  const { isFullScreen, id, name, isOpen, role, draftImageTool } = useMerchant();
  const location = useLocation();

  // Утасны off-canvas sidebar төлөв. Зам солигдоход автоматаар хаана.
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // ⚠️ Hyper theme-ийн CSS/JS нь `.leftside-menu`-г дардаг тул CSS override
  // ажиллахгүй байв. Inline style нь theme-ийн stylesheet дүрмийг дардаг —
  // off-canvas sidebar-ийг ЭНД inline-аар баталгаатай болгоно.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 991.98px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 991.98px)");
    const handler = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  const menuItems = getMenuItems(role, draftImageTool);

  const handleDisabledClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const closeMobile = () => setMobileOpen(false);

  // ⚠️ menuBody-г menuItems-ЭЭС ДООР зарлана (TDZ алдаа гаргахгүйн тулд).
  // Цэсний агуулга — desktop-д aside дотор, утсанд портал дотор ижил хэрэглэнэ.
  const menuBody = (
    <>
      <SidebarHeader
        id={id}
        name={name}
        isOpen={isOpen}
        openModal={() => openModal("settings")}
      />
      <nav className="side-nav list-unstyled">
        {menuItems.map((section) => (
          <SidebarSection
            key={section.title}
            title={section.title}
            items={section.items}
            onDisabledClick={handleDisabledClick}
            onNavigate={closeMobile}
          />
        ))}
        <SidebarNavItem
          label="Гарах"
          icon="uil-exit"
          onClick={(e) => {
            e.preventDefault();
            openModal("logout");
          }}
        />
      </nav>
      <SettingsModal show={currentModal === "settings"} handleClose={closeModal} />
      <LogoutModal show={currentModal === "logout"} handleClose={closeModal} />
    </>
  );

  if (isFullScreen) return null;

  return (
    <div className="wrapper d-flex">
      {/* Утасны дээд мөр — hamburger (зөвхөн жижиг дэлгэцэд CSS-ээр харагдана) */}
      <div className="e24-mobile-topbar">
        <button
          type="button"
          className="e24-ham btn btn-link"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Цэс"
        >
          <i className="uil uil-bars" />
        </button>
        <span className="e24-mobile-title text-truncate">{name}</span>
      </div>

      {isMobile ? (
        // Утас: порталаар document.body-д → эцэг элементийн transform/overflow/
        // z-index-ийн нөлөөнөөс ангид. Inline style тул CSS-тэй зөрчихгүй.
        createPortal(
          <>
            <div
              onClick={closeMobile}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.45)",
                zIndex: 99998,
                display: mobileOpen ? "block" : "none",
              }}
            />
            <aside
              className="leftside-menu menuitem-active"
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                bottom: 0,
                width: 260,
                maxWidth: "82vw",
                background: "#fff",
                overflowY: "auto",
                zIndex: 99999,
                transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
                transition: "transform 0.25s ease",
                boxShadow: "2px 0 12px rgba(0,0,0,0.15)",
                // ⚠️ Hyper theme: html[data-sidenav-size="full"] .leftside-menu
                // { opacity:0; margin-left:-260px } (JS-ээр утсанд тавьдаг) нь
                // манай порталыг ч нуудаг — inline-аар дардаг:
                opacity: 1,
                marginLeft: 0,
                display: "block",
                visibility: "visible",
              }}
            >
              {menuBody}
            </aside>
          </>,
          document.body
        )
      ) : (
        <aside className="leftside-menu menuitem-active">{menuBody}</aside>
      )}

      <main
        className="content-page pb-0 flex-grow-1"
        style={{
          marginLeft: isFullScreen && 0,
          backgroundColor: "#fafbfe",
        }}
      >
        {/* Шинэ захиалга татаж чадахгүй байгаа үед бүх хуудсанд харагдах банер */}
        <OrdersPollFailingBanner />
        <div className="content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

// "Шинэ захиалга ирэхгүй байна" банер — context/merchant.js-ийн pollOrders
// дараалан 2 удаа бүтэлгүйтэхэд гарч, дараагийн амжилттай poll-оор алга болно.
// sticky тул хуудсыг гүйлгэсэн ч дээд талд үлдэнэ.
function OrdersPollFailingBanner() {
  const { ordersPollFailing, ordersLoading, pollOrders } = useMerchant();

  if (!ordersPollFailing) return null;

  return (
    <div
      role="alert"
      className="bg-danger text-white fw-bold d-flex flex-wrap align-items-center justify-content-center gap-2 px-3 py-2"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 1020,
        // .content-page-ийн хажуугийн padding (0.75rem)-ийг нөхөж бүтэн өргөнөөр
        margin: "0 -0.75rem",
        fontSize: "1.05rem",
      }}
    >
      <i className="mdi mdi-wifi-off fs-3 lh-1" />
      <span>Шинэ захиалга ирэхгүй байна — интернэт холболтоо шалгана уу</span>
      <button
        type="button"
        className="btn btn-sm btn-light fw-bold ms-1"
        onClick={() => pollOrders()}
        disabled={ordersLoading}
      >
        {ordersLoading ? "Шалгаж байна..." : "Дахин шалгах"}
      </button>
    </div>
  );
}

function SidebarHeader({ id, name, isOpen, openModal }) {
  return (
    <div
      className="d-flex align-items-start me-auto sidebar-header"
      onClick={openModal}
      style={{ cursor: "pointer" }}
    >
      <img
        src={`https://et24-images.s3.ap-northeast-1.amazonaws.com/user/${id}.jpg`}
        className="me-2 rounded-circle"
        height={36}
        width={36}
        alt={name}
      />
      <div>
        <h5 className="mt-0 mb-0 font-15">
          <a className="text-reset">{name}</a>
        </h5>
        <p className="mt-1 lh-1 mb-0 text-muted font-12">
          <small
            className={`mdi mdi-circle ${
              isOpen ? "text-success" : "text-muted"
            }`}
          />{" "}
          {isOpen ? "Online" : "Offline"}
        </p>
      </div>
    </div>
  );
}

function SidebarSection({ title, items, onDisabledClick, onNavigate }) {
  return (
    <>
      <li className="side-nav-title">{title}</li>
      {items.map((item) => (
        <SidebarNavItem
          key={item.label}
          to={item.to}
          label={item.label}
          icon={item.icon}
          disabled={item.disabled}
          onClick={item.disabled ? onDisabledClick : onNavigate}
        />
      ))}
    </>
  );
}

function SidebarNavItem({
  to,
  label,
  icon,
  disabled = false,
  onClick = () => {},
}) {
  if (disabled) {
    return (
      <li className="side-nav-item">
        <a href="#" onClick={onClick} className="side-nav-link disabled">
          <i className={icon} />
          <span> {label} </span>
        </a>
      </li>
    );
  }

  if (!to) {
    return (
      <li className="side-nav-item">
        <a href="#" onClick={onClick} className="side-nav-link">
          <i className={icon} />
          <span> {label} </span>
        </a>
      </li>
    );
  }

  return (
    <li className="side-nav-item">
      <NavLink
        to={to}
        onClick={onClick}
        className={({ isActive }) =>
          `side-nav-link ${isActive ? "active text-primary fw-bold" : ""}`
        }
      >
        <i className={icon} />
        <span> {label} </span>
      </NavLink>
    </li>
  );
}
